"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const downshift_1 = __importDefault(require("downshift"));
const react_1 = require("react");
const lodash_1 = require("lodash");
const core_1 = require("@emotion/core");
const design_system_1 = require("@zapier/design-system");
const common_utils_1 = require("@zapier/common-utils");
const isDisabled = (props) => {
    return props.isDisabled;
};
const Styles = common_utils_1.labelStyles(__filename, {
    root: core_1.css({
        position: 'relative',
    }),
    buttonWrap: (props) => ({
        cursor: isDisabled(props) ? 'default' : 'pointer',
    }),
    buttonText: core_1.css({
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    }),
    placeholder: (props, derivedStated) => [
        {
            color: design_system_1.Colors.grey500,
        },
        !isDisabled(props) &&
            (derivedStated.isHovered || derivedStated.isFocused) &&
            common_utils_1.withModifier('hovered-or-focused', {
                color: design_system_1.Colors.blue500,
            }),
        props.isErrored &&
            common_utils_1.withModifier('errored', {
                color: design_system_1.Colors.red500,
            }),
        isDisabled(props) &&
            common_utils_1.withModifier('disabled', {
                color: design_system_1.Colors.grey500,
            }),
    ],
});
const buildAriaLabel = ({ label, value, placeholder, }) => {
    return [value, label, !value && placeholder].filter(Boolean).join(', ');
};
function Dropdown(props) {
    const parentRef = react_1.useRef(null);
    const itemToString = (item) => {
        return item ? props.getLabelForItem(item) : '';
    };
    const onChangeNoNull = (selection) => {
        if (selection) {
            props.onChange(selection);
        }
    };
    const stateReducer = (state, changes) => {
        switch (changes.type) {
            case downshift_1.default.stateChangeTypes.keyDownEscape:
                return {
                    isOpen: false,
                };
            case downshift_1.default.stateChangeTypes.mouseUp:
                return state;
            default:
                return changes;
        }
    };
    const renderButtonForItem = props.renderButtonForItem || props.getLabelForItem;
    return (common_utils_1.isolateComponent(downshift_1.default, { getLabelForItem: props.getLabelForItem, initialSelectedItem: props.initialSelectedItem, inputId: props.inputId, itemCount: props.items.length, itemToString: itemToString, onChange: onChangeNoNull, selectedItem: props.selectedItem, stateReducer: stateReducer }, ({ clearSelection, closeMenu, getInputProps, getItemProps, getMenuProps, getRootProps, highlightedIndex, isOpen, openMenu, selectItem, selectedItem, setHighlightedIndex, }) => {
        const hasSelectedItem = !!selectedItem;
        const renderIcon = () => typeof props.renderIcon === 'function'
            ? props.renderIcon(selectedItem)
            : undefined;
        const rootProps = lodash_1.omit(getRootProps(), ['aria-labelledby', 'role']);
        const onBlur = (e) => {
            if (props.onBlur) {
                const meta = {
                    didBlurWithin: parentRef.current &&
                        parentRef.current.contains(e.relatedTarget),
                    closeMenu,
                };
                props.onBlur(e, meta);
            }
            else {
                closeMenu();
            }
        };
        const Tag = props.isDisabled ? 'div' : 'button';
        return (common_utils_1.isolateComponent("div", Object.assign({ css: Styles.root }, rootProps, { onBlur: onBlur, ref: parentRef }),
            common_utils_1.isolateComponent("div", { css: Styles.buttonWrap(props) },
                common_utils_1.isolateComponent(design_system_1.Input, Object.assign({ onClick: () => {
                        if (isOpen === true) {
                            closeMenu();
                        }
                    } }, props, design_system_1.camelCaseAriaAttrs(getInputProps()), { canClickIconAfter: false, id: props.inputId, isDisabled: isDisabled(props), renderIconAfter: () => (common_utils_1.isolateComponent(design_system_1.Icon, { name: "upAndDownArrows", size: 24 })), renderIconBefore: renderIcon }), (inputProps, derivedState) => props.hasCustomContent ? (props.renderCustomContent(Object.assign(Object.assign({}, inputProps), { closeMenu,
                    openMenu }))) : (common_utils_1.isolateComponent(Tag, Object.assign({}, {
                    'aria-activedescendant': inputProps['aria-activedescendant'],
                }, { "aria-controls": inputProps['aria-controls'], "aria-expanded": isOpen ? 'true' : 'false', "aria-haspopup": "true", "aria-invalid": inputProps['aria-invalid'], "aria-label": buildAriaLabel({
                        label: inputProps['aria-label'],
                        placeholder: props.placeholder,
                        value: hasSelectedItem
                            ? props.getLabelForItem(selectedItem)
                            : undefined,
                    }), css: [inputProps.css], id: inputProps.id, onBlur: onBlur, onChange: onChangeNoNull, onClick: isDisabled(props) ? undefined : openMenu, onFocus: props.onFocus, onKeyDown: inputProps.onKeyDown, type: "button" }), hasSelectedItem ? (common_utils_1.isolateComponent("span", { css: Styles.buttonText }, renderButtonForItem(selectedItem))) : (common_utils_1.isolateComponent("span", { css: [
                        Styles.buttonText,
                        Styles.placeholder(props, derivedState),
                    ] }, props.placeholder)))))),
            isOpen &&
                props.renderFloatingMenu({
                    ariaLabel: props.menuAriaLabel,
                    clearSelection,
                    closeMenu,
                    items: props.items.filter(item => Object.values(item).some(value => value !== '' && value !== undefined)),
                    highlightedIndex,
                    getKeyForItem: props.getKeyForItem,
                    getLabelForItem: props.getLabelForItem,
                    getItemProps,
                    getMenuProps,
                    getInputProps,
                    renderIcon: props.renderIcon,
                    selectItem,
                    selectedItem,
                    setHighlightedIndex,
                })));
    }));
}
Dropdown.defaultProps = {
    color: 'grey100',
    size: 'small',
    getLabelForItem: (item) => item.label,
    renderFloatingMenu: (props) => common_utils_1.isolateComponent(design_system_1.FloatingMenu, Object.assign({}, props)),
    hasCustomContent: false,
    renderCustomContent: () => { },
};
exports.default = Dropdown;
